/**
 * Memory Sandbox — one screen, three regions: what you can make, what exists,
 * and one bench for working something out.
 */

.sb {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto auto;
  height: 100%;
  background: var(--paper);
}

/* -- top bar ---------------------------------------------------------- */

.sb__bar {
  display: flex;
  align-items: baseline;
  gap: var(--sp-4);
  padding: var(--sp-3) var(--sp-4);
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}

.sb__title { margin: 0; font-size: var(--fs-lg); font-weight: 800; }
.sb__status { margin: 0; color: var(--ink-faint); font-size: var(--fs-xs); }
.sb__actions { margin-left: auto; display: flex; gap: var(--sp-2); }

.sb__actions button {
  font: inherit;
  font-size: var(--fs-sm);
  padding: var(--sp-1) var(--sp-3);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  background: var(--surface-raised);
  cursor: pointer;
}
.sb__actions button:disabled { opacity: 0.4; cursor: default; }

/* -- body ------------------------------------------------------------- */

.sb__body {
  display: grid;
  grid-template-columns: 210px minmax(0, 1fr);
  gap: var(--sp-4);
  padding: var(--sp-4);
  min-height: 0;
}

@media (max-width: 860px) {
  .sb__body { grid-template-columns: minmax(0, 1fr); }
  .sb__palette { display: flex; flex-wrap: wrap; align-items: flex-end; }
}

.sb__palette { display: grid; gap: var(--sp-2); align-content: start; }

.sb__heading {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-xs);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-faint);
  flex-basis: 100%;
}
.sb__heading:first-child { margin-top: 0; }

.sb__tip {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-xs);
  color: var(--ink-soft);
  line-height: 1.5;
}

.sb__canvas { min-width: 0; overflow: auto; }

/* -- makers ----------------------------------------------------------- */

.maker {
  display: grid;
  gap: var(--sp-1);
  padding: var(--sp-2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface-raised);
}

.maker__grab {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font: inherit;
  font-weight: 700;
  text-align: left;
  padding: var(--sp-1);
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: inherit;
  /* It is a thing you pick up. */
  cursor: grab;
  touch-action: none;
}
.maker__grab:active { cursor: grabbing; }
.maker__grab:hover { background: var(--pip-soft); }

.maker__glyph {
  font-family: var(--mono);
  font-size: var(--fs-xs);
  font-weight: 800;
  padding: 2px var(--sp-1);
  border-radius: var(--radius-sm);
  background: var(--surface-sunk);
  color: var(--ink-soft);
  flex: none;
}

.maker__name { font-size: var(--fs-sm); }

.maker input {
  font: inherit;
  font-family: var(--mono);
  font-size: var(--fs-sm);
  width: 100%;
  padding: var(--sp-1) var(--sp-2);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  background: var(--paper);
}

.maker__hint { font-size: var(--fs-xs); color: var(--ink-faint); }

/* A name is a label, so the maker for one looks like the tags in the canvas. */
.maker--tag {
  border-style: dashed;
  border-color: var(--name-tag);
  background: var(--name-tag-soft);
}
.maker--tag .maker__grab { color: var(--name-tag); }
.maker--tag .maker__glyph { background: transparent; }

/* -- the bench -------------------------------------------------------- */

.sb__bench { padding: 0 var(--sp-4) var(--sp-3); }

.bench {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
}

.well {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  min-width: 140px;
  min-height: 42px;
  padding: var(--sp-2) var(--sp-3);
  border: 2px dashed var(--border-strong);
  border-radius: var(--radius);
  color: var(--ink-faint);
  font-size: var(--fs-sm);
  background: var(--paper);
}

.well[data-filled='true'] {
  border-style: solid;
  border-color: var(--object-int);
  color: var(--ink);
  font-weight: 700;
  background: var(--surface-raised);
}

.well__clear {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: var(--ink-faint);
  cursor: pointer;
  font-size: var(--fs-md);
  line-height: 1;
  padding: 0 var(--sp-1);
}

.bench select {
  font: inherit;
  font-size: var(--fs-sm);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  background: var(--surface-raised);
}

.bench__go {
  font: inherit;
  font-weight: 700;
  padding: var(--sp-2) var(--sp-4);
  border: 1px solid var(--pip);
  border-radius: var(--radius);
  background: var(--pip);
  color: #fff;
  cursor: pointer;
}
.bench__go:disabled { opacity: 0.4; cursor: default; }

.bench__note {
  margin: 0;
  flex: 1 1 220px;
  font-size: var(--fs-xs);
  color: var(--ink-soft);
}

/* -- the running commentary ------------------------------------------- */

.sb__say {
  margin: 0;
  padding: var(--sp-2) var(--sp-4) var(--sp-4);
  font-size: var(--fs-sm);
  color: var(--ink-soft);
  min-height: 2.4em;
}
.sb__problem { color: var(--bad); }

/* -- naming prompt ---------------------------------------------------- */

.prompt {
  position: fixed;
  z-index: 300;
  left: 50%;
  top: 30%;
  transform: translateX(-50%);
  padding: var(--sp-4);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  background: var(--surface-raised);
  box-shadow: var(--shadow-lift);
}

.prompt form { display: flex; align-items: center; gap: var(--sp-2); }
.prompt label { font-weight: 700; }

.prompt input {
  font: inherit;
  font-family: var(--mono);
  padding: var(--sp-2);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  width: 12ch;
}

.prompt button {
  font: inherit;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  background: var(--surface);
  cursor: pointer;
}
.prompt button[type='submit'] { background: var(--pip); border-color: var(--pip); color: #fff; }
.prompt button:disabled { opacity: 0.4; cursor: default; }

.prompt__bad { margin: var(--sp-2) 0 0; font-size: var(--fs-xs); color: var(--bad); }

/* What is currently picked up, for the click-only path. */
.sb__held {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  margin: 0 var(--sp-4);
  padding: var(--sp-2) var(--sp-3);
  border-radius: var(--radius);
  background: var(--pip-soft);
  color: var(--pip-deep);
  font-size: var(--fs-sm);
  font-weight: 700;
}

.sb__held button {
  font: inherit;
  font-weight: 400;
  margin-left: auto;
  padding: var(--sp-1) var(--sp-2);
  border: 1px solid var(--pip);
  border-radius: var(--radius-sm);
  background: var(--surface-raised);
  color: var(--pip-deep);
  cursor: pointer;
}

.well[data-placeable='true'] { border-color: var(--pip); border-style: solid; }

.well__place {
  font: inherit;
  font-weight: 700;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--pip-deep);
  cursor: pointer;
}

/* The tag maker sits on a tinted background, where --ink-faint drops to
   4.2:1. The name colour is already verified against this exact tint. */
.maker--tag .maker__hint { color: var(--name-tag); }
